import { gql, useMutation } from '@apollo/client';
import IconMarkdownSolid from '@apollo/icons/default/IconMarkdownSolid.svg';
import {
  Button,
  ButtonGroup,
  Checkbox,
  FormControl,
  FormHelperText,
  FormLabel,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  Textarea,
} from '@apollo/orbit';
import { useFormik } from 'formik';
import React, { useCallback, useState } from 'react';
import * as Yup from 'yup';

import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { ReviewDecision } from 'src/lib/graphqlTypes/types';

import { ProposalUpdateRequestedReviewersMutationGQL } from '../requestedReviewersSection/RequestedReviewersSection';

const validationSchema = Yup.object().shape({
  approved: Yup.boolean(),
  comment: Yup.string(),
});

type FormValues = Yup.InferType<typeof validationSchema>;

export const ProposalReviewModalProposalReviewFragment = gql`
  fragment ProposalReviewModalProposalReviewFragment on ProposalReview {
    id
    comment {
      id
      message
    }
    decision
    createdAt
    createdBy {
      id
    }
    isDismissed
  }
`;

export const ProposalReviewModal = ({
  proposalId,
  revisionId,
  currentUserReview,
  isRequestedReviewer,
  userId,
  isOpen,
  onClose,
}: {
  proposalId: string;
  revisionId: string | undefined;
  currentUserReview?:
    | GraphQLTypes.ProposalReviewModalProposalReviewFragment
    | undefined;
  isRequestedReviewer: boolean;
  userId: string;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const [errorMessage, setErrorMessage] = useState<string>();

  const [addReviewMutation] = useMutation<
    GraphQLTypes.ProposalUpsertReviewMutation,
    GraphQLTypes.ProposalUpsertReviewMutationVariables
  >(gql`
    # 🚨IMPORTANT🚨: Do not rename this query. We use it in our Proposals SLO by name.
    mutation ProposalUpsertReviewMutation(
      $proposalId: ID!
      $input: UpsertReviewInput!
    ) {
      proposal(id: $proposalId) {
        ... on ProposalMutation {
          upsertReview(input: $input) {
            ... on Proposal {
              id
              requestedReviewers {
                currentReview {
                  id
                  decision
                }
                user {
                  id
                }
              }
              reviews {
                id
                comment {
                  id
                  message
                  createdAt
                  createdBy {
                    id
                    ... on User {
                      fullName
                      avatarUrl
                    }
                  }
                }
                decision
              }
            }
            ... on PermissionError {
              message
            }
            ... on ValidationError {
              message
            }
          }
        }
      }
    }
  `);

  const [updateReviewers] = useMutation<
    GraphQLTypes.ProposalUpdateRequestedReviewersMutation,
    GraphQLTypes.ProposalUpdateRequestedReviewersMutationVariables
  >(ProposalUpdateRequestedReviewersMutationGQL);

  const {
    values,
    isSubmitting,
    isValid,
    dirty,
    handleSubmit,
    handleChange,
    resetForm,
  } = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      approved:
        !currentUserReview?.isDismissed &&
        currentUserReview?.decision === ReviewDecision.APPROVED,
      comment: !currentUserReview?.isDismissed
        ? (currentUserReview?.comment?.message ?? '')
        : '',
    },
    onSubmit: async ({ approved, comment }) => {
      if (!revisionId) return;

      let skipReview = false;
      if (!isRequestedReviewer) {
        const { data: updateReviewersData } = await updateReviewers({
          variables: {
            proposalId,
            input: {
              reviewerUserIdsToAdd: [userId],
            },
          },
        });

        if (
          updateReviewersData?.proposal.__typename === 'PermissionError' ||
          (updateReviewersData?.proposal.__typename === 'ProposalMutation' &&
            updateReviewersData?.proposal.updateRequestedReviewers
              .__typename === 'PermissionError')
        ) {
          setErrorMessage(
            'You do not have permission to do this. Please contact your graph admin for assistance.',
          );
          skipReview = true;
        } else if (
          updateReviewersData?.proposal.__typename !== 'ProposalMutation' ||
          updateReviewersData.proposal.updateRequestedReviewers.__typename !==
            'Proposal'
        ) {
          setErrorMessage('An error has occurred');
          skipReview = true;
        }
      }

      if (!skipReview) {
        const { data: responseData } = await addReviewMutation({
          variables: {
            proposalId,
            input: {
              decision: approved
                ? ReviewDecision.APPROVED
                : ReviewDecision.NOT_APPROVED,
              comment,
              revisionId,
            },
          },
        });

        if (
          responseData?.proposal.__typename === 'ProposalMutation' &&
          responseData.proposal.upsertReview?.__typename === 'Proposal'
        ) {
          onClose();
          resetForm({});
          setErrorMessage('');
        } else if (
          responseData?.proposal.__typename === 'PermissionError' ||
          (responseData?.proposal.__typename === 'ProposalMutation' &&
            responseData.proposal.upsertReview.__typename === 'PermissionError')
        ) {
          setErrorMessage(
            'You do not have permission to do this. Please contact your graph admin for assistance.',
          );
        } else {
          setErrorMessage('An error has occurred');
        }
      }
    },
  });

  const handleClose = useCallback(() => {
    onClose();
    resetForm({});
    setErrorMessage('');
  }, [onClose, resetForm]);

  return (
    <Modal size="xl" isOpen={isOpen} onClose={handleClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Add your review</ModalTitle>
        </ModalHeader>
        <ModalBody>
          {!!errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
          <FormControl>
            <FormLabel>Comment</FormLabel>
            <Textarea
              name="comment"
              value={values.comment}
              placeholder="Give your team some feedback about the proposed change"
              onChange={handleChange}
            />
            <IconMarkdownSolid className="absolute bottom-3 right-3 z-10 text-icon-primary" />
          </FormControl>
          <FormControl>
            <Checkbox
              isChecked={values.approved}
              name="approved"
              className="font-semibold"
              onChange={handleChange}
            >
              Approve proposal
            </Checkbox>

            <FormHelperText>
              Let your team know these changes can be implemented.
            </FormHelperText>
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup>
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              isDisabled={!isValid || !dirty}
              onClick={() => handleSubmit()}
              isLoading={isSubmitting}
              loadingText="Adding review"
            >
              Add review
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
